import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Drawer, Typography, Tag, Slider, Button } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import * as d3 from 'd3';
import { IBrandEquityScoreResponse } from '../../services/nestjs-base-api/types';
import { useGetSystemSettingQuery, useReanalyzeElementMutation } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import StatementDetailModal from './StatementDetailModal';
import ReactMarkdown from 'react-markdown';

const { Title, Text } = Typography;

// Helper function to create gradient slider with position indicator
const createBipolarProgressBar = (score: number | null, backgroundColor: string, fillColor: string, elementName?: string) => {
    // Handle null scores (no data available)
    if (score === null) {
        return `
            <div style="
                display: flex;
                align-items: center;
                gap: 8px;
                height: 32px;
            ">
                <div style="
                    position: relative;
                    width: 100px;
                    height: 16px;
                    display: flex;
                    align-items: center;
                ">
                    <div style="
                        width: 100%;
                        height: 8px;
                        background: #e5e7eb;
                        border-radius: 4px;
                        border: 1px solid rgba(0, 0, 0, 0.1);
                    "></div>
                </div>
                <div style="
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 50px;
                    height: 26px;
                    padding: 0 4px;
                    color: #8c8c8c;
                    font-size: 14px;
                    font-weight: 500;
                    font-style: italic;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                ">N/A</div>
            </div>
        `;
    }

    const isNegative = score < 0;
    const absScore = Math.abs(score);

    // Calculate visual elements
    const barWidth = 100;
    const barHeight = 8;
    const displayScore = score > 0 ? `+${score.toFixed(0)}%` : score === 0 ? '0%' : `${score.toFixed(0)}%`;

    // Calculate indicator position based on element type
    let indicatorPosition: number;
    if (elementName === 'Price Deals') {
        // Price Deals ranges from -100% to 0%, so map to 0 to 100px
        indicatorPosition = ((score + 100) / 100) * barWidth;
    } else {
        // Standard bipolar scale from -100% to +100%, map to 0 to 100px
        indicatorPosition = ((score + 100) / 200) * barWidth;
    }
    
    // Make background much more visible
    let enhancedBackground = backgroundColor;
    if (backgroundColor.includes('0.2)')) {
        enhancedBackground = backgroundColor.replace('0.2)', '0.6)'); // Very visible
    } else {
        // Fallback: create a visible background based on fillColor
        enhancedBackground = `${fillColor}60`; // 38% opacity using hex notation
    }
    
    // Create gradient slider with position indicator
    const svgElement = `
        <div style="
            display: flex;
            align-items: center;
            gap: 8px;
            height: 32px;
        ">
            <!-- Gradient Slider Container -->
            <div style="
                position: relative;
                width: ${barWidth}px;
                height: ${barHeight + 8}px;
                display: flex;
                align-items: center;
            ">
                <!-- Gradient Track -->
                <div style="
                    position: relative;
                    width: 100%;
                    height: ${barHeight}px;
                    background: ${elementName === 'Price Deals' ?
                        'linear-gradient(to right, #ef4444 0%, #f59e0b 30%, #eab308 60%, #84cc16 85%, #22c55e 100%)' :
                        'linear-gradient(to right, #ef4444 0%, #f59e0b 25%, #eab308 40%, #84cc16 60%, #22c55e 75%, #10b981 100%)'
                    };
                    border-radius: 4px;
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
                ">
                    <!-- Zero marker line -->
                    <div style="
                        position: absolute;
                        left: ${elementName === 'Price Deals' ? '100%' : '50%'};
                        top: -1px;
                        bottom: -1px;
                        width: 1px;
                        background: rgba(0, 0, 0, 0.4);
                        transform: translateX(-50%);
                        border-radius: 1px;
                    "></div>
                </div>
                
                <!-- Position Indicator -->
                <div style="
                    position: absolute;
                    left: ${indicatorPosition}px;
                    top: -3px;
                    width: 3px;
                    height: ${barHeight + 10}px;
                    background: #1f2937;
                    border-radius: 2px;
                    transform: translateX(-50%);
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
                    transition: left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                "></div>
            </div>
            
            <!-- Clean Number Display -->
            <div style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                min-width: 50px;
                height: 26px;
                padding: 0 4px;
                color: #1f2937;
                font-size: 14px;
                font-weight: 500;
                letter-spacing: -0.02em;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                transition: all 0.3s ease;
            ">
                ${displayScore}
            </div>
        </div>
    `;
    
    return svgElement;
};

const StrengthSlider = ({ value, label }: { value: number | null; label?: string }) => {
    useEffect(() => {
        // Add custom styles to hide the track elements and add midline
        const styleId = 'strength-slider-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                .strength-slider .ant-slider-track,
                .strength-slider .ant-slider-step,
                .strength-slider .ant-slider-dot {
                    display: none !important;
                    visibility: hidden !important;
                    opacity: 0 !important;
                }
                .strength-slider .ant-slider-handle::after,
                .strength-slider .ant-slider-handle::before,
                .strength-slider .ant-slider-handle-dragging::after,
                .strength-slider .ant-slider-handle-dragging::before {
                    display: none !important;
                    content: none !important;
                    width: 0 !important;
                    height: 0 !important;
                    box-shadow: none !important;
                    background: none !important;
                }
                .strength-slider {
                    position: relative;
                }
                .strength-slider::after {
                    content: '';
                    position: absolute;
                    top: 4px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 2px;
                    height: 20px;
                    background-color: #000;
                    opacity: 0.3;
                    z-index: 5;
                    pointer-events: none;
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    // All statements now use the -100 to +100 scale
    // Map the value to slider position (0-100 range for display)
    const sliderValue = ((value || 0) + 100) / 2;

    return (
        <div style={{ width: '100%' }}>
            {label && <div style={{ marginBottom: '8px', fontSize: '14px' }}>{label}</div>}
            <Slider
                value={sliderValue}
                disabled
                min={0}
                max={100}
                tooltip={{ open: false }}
                className="strength-slider"
                style={{ margin: '12px 0 8px 0' }}
                styles={{
                    track: { 
                        display: 'none !important',
                        visibility: 'hidden',
                        opacity: 0,
                        background: 'transparent',
                        border: 'none'
                    },
                    rail: {
                        background: value === null 
                            ? '#6b7280'
                            : 'linear-gradient(to right, #ef4444, #eab308, #22c55e)',
                        height: '20px',
                        borderRadius: '5px',
                        border: '1px solid #374151',
                    },
                    handle: {
                        backgroundColor: '#000',
                        border: 'none',
                        width: '4px',
                        height: '30px',
                        borderRadius: '2px',
                        marginTop: '-5px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        opacity: value === null ? 0 : 1,
                        visibility: value === null ? 'hidden' : 'visible',
                        zIndex: 10
                    }
                }}
            />
        </div>
    );
};

const ResponseSpeedometer = ({ responseCount }: { responseCount: number }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    
    useEffect(() => {
        if (!svgRef.current) return;
        
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        
        const width = 200;
        const height = 120;
        const radius = 80;
        
        const g = svg.append('g')
            .attr('transform', `translate(${width / 2}, ${height - 10})`);
        
        // Define angle range (180 degrees for semi-circle)
        const startAngle = -Math.PI;
        const endAngle = 0;
        
        // Create gradient definition for both outline and fill
        const defs = svg.append('defs');
        const gradient = defs.append('linearGradient')
            .attr('id', 'gauge-gradient')
            .attr('gradientUnits', 'userSpaceOnUse')
            .attr('x1', -radius)
            .attr('y1', 0)
            .attr('x2', radius)
            .attr('y2', 0);
        
        gradient.append('stop')
            .attr('offset', '0%')
            .attr('stop-color', '#f97316'); // Orange
        
        gradient.append('stop')
            .attr('offset', '50%')
            .attr('stop-color', '#eab308'); // Yellow
        
        gradient.append('stop')
            .attr('offset', '100%')
            .attr('stop-color', '#22c55e'); // Green
        
        // Create arc generator for filled portion
        const arc = d3.arc()
            .innerRadius(radius - 20)
            .outerRadius(radius)
            .startAngle(startAngle);
        
        // Draw gauge outline (full 180 degrees) - create manually
        const outerRadius = radius;
        const innerRadius = radius - 20;
        
        // Create full semicircle path manually
        const pathData = [
            `M ${-outerRadius} 0`, // Move to leftmost point of outer arc
            `A ${outerRadius} ${outerRadius} 0 0 1 ${outerRadius} 0`, // Outer arc from left to right
            `L ${innerRadius} 0`, // Line to inner right
            `A ${innerRadius} ${innerRadius} 0 0 0 ${-innerRadius} 0`, // Inner arc from right to left
            `Z` // Close path
        ].join(' ');
        
        g.append('path')
            .attr('d', pathData)
            .style('fill', 'url(#gauge-gradient)')
            .style('opacity', 0.3)
            .style('stroke', '#9ca3af')
            .style('stroke-width', 1);
        
        // Draw filled portion with gradient
        if (responseCount > 0) {
            const maxScale = 60; // Max scale for display
            const fillAngle = startAngle + (Math.min(responseCount, maxScale) / maxScale) * Math.PI;
            g.append('path')
                .datum({ endAngle: fillAngle })
                .attr('d', arc as any)
                .style('fill', 'url(#gauge-gradient)')
                .style('opacity', 0.9);
        }
        
        // Add segment labels
        g.append('text')
            .attr('x', -radius + 10)
            .attr('y', -5)
            .attr('text-anchor', 'middle')
            .style('font-size', '11px')
            .style('fill', '#6b7280')
            .style('font-weight', '600')
            .text('Low');
        
        g.append('text')
            .attr('x', 0)
            .attr('y', -radius + 15)
            .attr('text-anchor', 'middle')
            .style('font-size', '11px')
            .style('fill', '#6b7280')
            .style('font-weight', '600')
            .text('Moderate');
        
        g.append('text')
            .attr('x', radius - 10)
            .attr('y', -5)
            .attr('text-anchor', 'middle')
            .style('font-size', '11px')
            .style('fill', '#6b7280')
            .style('font-weight', '600')
            .text('Good');
        
        // Add subtle tick marks (without numbers)
        const maxScale = 60; // Max scale for display
        const ticks = [0, 20, 40];
        ticks.forEach(tick => {
            const angle = startAngle + (tick / maxScale) * Math.PI;
            const x1 = Math.cos(angle) * (radius - 20);
            const y1 = Math.sin(angle) * (radius - 20);
            const x2 = Math.cos(angle) * (radius - 25);
            const y2 = Math.sin(angle) * (radius - 25);
            
            g.append('line')
                .attr('x1', x1)
                .attr('y1', y1)
                .attr('x2', x2)
                .attr('y2', y2)
                .style('stroke', '#9ca3af')
                .style('stroke-width', 1.5);
        });
        
        // Add needle
        const needleAngle = startAngle + (Math.min(responseCount, maxScale) / maxScale) * Math.PI;
        const needleLength = radius - 25;
        
        // Needle shadow
        g.append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', Math.cos(needleAngle) * needleLength)
            .attr('y2', Math.sin(needleAngle) * needleLength)
            .style('stroke', 'rgba(0,0,0,0.2)')
            .style('stroke-width', 4)
            .style('stroke-linecap', 'round')
            .attr('transform', 'translate(1, 1)');
        
        // Needle
        g.append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', Math.cos(needleAngle) * needleLength)
            .attr('y2', Math.sin(needleAngle) * needleLength)
            .style('stroke', '#1f2937')
            .style('stroke-width', 3)
            .style('stroke-linecap', 'round');
        
        // Center dot
        g.append('circle')
            .attr('r', 6)
            .style('fill', '#1f2937');
        
        // Response count display
        g.append('text')
            .attr('x', 0)
            .attr('y', 25)
            .attr('text-anchor', 'middle')
            .style('font-size', '18px')
            .style('font-weight', 'bold')
            .style('fill', '#1f2937')
            .text(responseCount);
        
        g.append('text')
            .attr('x', 0)
            .attr('y', 40)
            .attr('text-anchor', 'middle')
            .style('font-size', '11px')
            .style('fill', '#6b7280')
            .text('responses');
        
    }, [responseCount]);
    
    const getStatus = () => {
        if (responseCount <= 20) return { text: 'Low Response Count', color: '#ef4444' };
        if (responseCount <= 40) return { text: 'Moderate Response Count', color: '#eab308' };
        return { text: 'Good Response Count', color: '#22c55e' };
    };
    
    const status = getStatus();
    
    return (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            padding: '8px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            width: '100%'
        }}>
            <svg ref={svgRef} width={200} height={120} />
            <div style={{
                marginTop: '8px',
                fontSize: '13px',
                fontWeight: '600',
                color: status.color
            }}>
                {status.text}
            </div>
            {responseCount <= 20 && (
                <div style={{
                    marginTop: '8px',
                    fontSize: '11px',
                    color: '#6b7280',
                    textAlign: 'center',
                    maxWidth: '180px'
                }}>
                    Results may not be statistically reliable
                </div>
            )}
        </div>
    );
};

interface BrandEquityD3VisualizationProps {
    results: IBrandEquityScoreResponse;
    isModal?: boolean;
    responseCountThreshold?: number; // Configurable threshold for low response count
    isAdmin?: boolean;
    organisationId?: number; // Required for admin reanalysis
}

interface Node {
    id: string;
    name: string;
    type: 'top' | 'latent' | 'qc';
    shape?: 'star' | 'hexagon' | 'square' | 'pill' | 'circle' | 'diamond';
    level: number;
    color: string;
    x?: number;
    y?: number;
    score?: number | null; // null when no data exists
}

interface Link {
    source: string;
    target: string;
    type: 'le-to-le' | 'qc-to-le';
}

const BrandEquityD3Visualization: React.FC<BrandEquityD3VisualizationProps> = ({ 
    results, 
    isModal = false,
    responseCountThreshold = 30, // Default threshold of 30 responses
    isAdmin = false,
    organisationId
}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const intl = useIntl();
    const { user } = useAuth();
    const [selectedNode, setSelectedNode] = useState<string | null>(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerData, setDrawerData] = useState<Node | null>(null);

    // Navigation history state
    const [navigationHistory, setNavigationHistory] = useState<Node[]>([]);
    const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
    const currentTransformRef = useRef<any>(null);
    const [zoomEnabled, setZoomEnabled] = useState(false);
    const zoomBehaviorRef = useRef<any>(null);
    const [statementModalVisible, setStatementModalVisible] = useState(false);
    const [selectedStatement, setSelectedStatement] = useState<any>(null);
    const [currentAiSummary, setCurrentAiSummary] = useState<string | undefined>(undefined);
    const [isReanalyzing, setIsReanalyzing] = useState(false);

    // Re-analyze element mutation
    const [reanalyzeElement] = useReanalyzeElementMutation();

    // Fetch element descriptions from system settings
    const { data: elementDescriptionsData } = useGetSystemSettingQuery({
        jwtToken: user?.jwtToken || '',
        settingName: 'ELEMENT_DESCRIPTIONS'
    }, {
        skip: !user?.jwtToken
    });

    // Parse element descriptions
    const dynamicElementDescriptions = useMemo(() => {
        try {
            if (elementDescriptionsData) {
                let parsed;
                
                // Handle different response formats
                if (typeof elementDescriptionsData === 'string') {
                    // Try to parse the string
                    try {
                        parsed = JSON.parse(elementDescriptionsData);
                        
                        // Check if the result is still a string (double-encoded JSON)
                        if (typeof parsed === 'string') {
                            parsed = JSON.parse(parsed);
                        }
                    } catch (parseError) {
                        console.error('Failed to parse elementDescriptionsData string:', parseError);
                        return {};
                    }
                } else if (typeof elementDescriptionsData === 'object') {
                    parsed = elementDescriptionsData;
                } else {
                    return {};
                }
                
                // Handle different possible structures
                if (parsed && typeof parsed === 'object') {
                    // If it has an 'elements' property, use that
                    if (parsed.elements && typeof parsed.elements === 'object') {
                        return parsed.elements;
                    }
                    // If it has a 'value' property (API might wrap the data), try to parse that
                    else if (parsed.value) {
                        try {
                            const valueData = typeof parsed.value === 'string' ? JSON.parse(parsed.value) : parsed.value;
                            return valueData.elements || {};
                        } catch (e) {
                            console.error('Failed to parse value property:', e);
                        }
                    }
                    // If the parsed object itself looks like the elements structure, use it directly
                    else if (Object.keys(parsed).length > 0 && !parsed.elements) {
                        // Check if it looks like an elements object (has properties with mouseover/sidebar)
                        const firstKey = Object.keys(parsed)[0];
                        if (parsed[firstKey] && (parsed[firstKey].mouseover !== undefined || parsed[firstKey].sidebar !== undefined)) {
                            return parsed;
                        }
                    }
                }
                
                return {};
            }
        } catch (error) {
            console.error('Failed to parse element descriptions:', error);
        }
        return {};
    }, [elementDescriptionsData]);
    const [selectedStatementCategory, setSelectedStatementCategory] = useState<string>('');

    // Reset zoom enabled state when modal state changes
    useEffect(() => {
        setZoomEnabled(false);
    }, [isModal]);

    // Reset AI summary when drawer data changes
    useEffect(() => {
        setCurrentAiSummary(undefined);
    }, [drawerData]);

    // Re-apply or remove zoom behavior when zoom state changes
    useEffect(() => {
        if (svgRef.current && zoomBehaviorRef.current) {
            const svg = d3.select(svgRef.current);
            if (zoomEnabled) {
                svg.call(zoomBehaviorRef.current);
            } else {
                // Remove zoom behavior by applying an empty zoom
                svg.on('.zoom', null);
            }
        }
    }, [zoomEnabled]);

    // Configuration for initial view - ADJUST THESE VALUES
    const viewConfig = useMemo(() => ({
        inline: {
            zoom: 0.6,      // Scale factor (0.3 to 3)
            offsetX: -450,     // Horizontal offset in pixels (negative = left, positive = right)
            offsetY: -420,    // Vertical offset in pixels (positive = up, negative = down)
            hideHorizontalScroll: true,
            hideVerticalScroll: true
        },
        modal: {
            zoom: 0.75,      // Scale factor (0.3 to 3)
            offsetX: -150,     // Horizontal offset in pixels (negative = left, positive = right)
            offsetY: -270,     // Vertical offset in pixels (positive = up, negative = down)
            hideHorizontalScroll: true,
            hideVerticalScroll: true
        }
    }), []);

    // Connection data structure (same as your CSV but without weights)
    const connectionData = useMemo(() => [
        { latentElement: 'Brand Attachment', input: 'Brand Self Connection', inputType: 'QC' },
        { latentElement: 'Brand Attachment', input: 'Prominence', inputType: 'QC' },
        { latentElement: 'Brand Attachment', input: 'Self Congruence', inputType: 'QC' },
        { latentElement: 'Quality Value Tradeoff (PQV)', input: 'Perceived Value for the Cost', inputType: 'QC' },
        { latentElement: 'Quality Value Tradeoff (PQV)', input: 'Product Richness', inputType: 'QC' },
        { latentElement: 'Willingness to Pay a Premium', input: 'Brand Attachment', inputType: 'LE' },
        { latentElement: 'Willingness to Pay a Premium', input: 'Quality Value Tradeoff (PQV)', inputType: 'LE' },
        { latentElement: 'Willingness to Pay a Premium', input: 'Uniqueness', inputType: 'QC' },
        { latentElement: 'Behavioural Loyalty', input: 'Alignment Depth', inputType: 'LE' },
        { latentElement: 'Behavioural Loyalty', input: 'Willingness to Pay a Premium', inputType: 'LE' },
        { latentElement: 'Behavioural Loyalty', input: 'Repurchase Intention', inputType: 'QC' },
        { latentElement: 'Alignment Depth', input: 'Staff Behaviour', inputType: 'QC' },
        { latentElement: 'Alignment Depth', input: 'Brand Environment', inputType: 'QC' },
        { latentElement: 'Attitudinal Loyalty', input: 'Standardised NPS', inputType: 'QC' },
        { latentElement: 'Attitudinal Loyalty', input: 'User Congruence', inputType: 'QC' },
        { latentElement: 'Attitudinal Loyalty', input: 'Brand Identification', inputType: 'QC' },
        { latentElement: 'Attitudinal Loyalty', input: 'Brand Trust', inputType: 'QC' },
        { latentElement: 'Market Awareness', input: 'Unprompted Brand Awareness', inputType: 'QC' },
        { latentElement: 'Market Awareness', input: 'Prompted Brand Awareness', inputType: 'QC' },
        { latentElement: 'Associations', input: 'Symbolic Recall', inputType: 'QC' },
        { latentElement: 'Associations', input: 'Uniqueness', inputType: 'QC' },
        { latentElement: 'Associations', input: 'Brand Trust', inputType: 'QC' },
        { latentElement: 'Associations', input: 'Brand Valence', inputType: 'QC' },
        { latentElement: 'Associations', input: 'Brand Empathy', inputType: 'QC' },
        { latentElement: 'Associations', input: 'Brand Environment', inputType: 'QC' },
        { latentElement: 'Associations', input: 'Advertising Content', inputType: 'QC' },
        { latentElement: 'Associations', input: 'Price Deals', inputType: 'QC' },
        { latentElement: 'Perceived Quality', input: 'Product Performance', inputType: 'QC' },
        { latentElement: 'Perceived Quality', input: 'Product Richness', inputType: 'QC' },
        { latentElement: 'Perceived Quality', input: 'Distribution Intensity', inputType: 'QC' },
        { latentElement: 'Perceived Quality', input: 'Price', inputType: 'QC' },
        { latentElement: 'Perceived Quality', input: 'Price Deals', inputType: 'QC' },
        { latentElement: 'Perceived Quality', input: 'Brand Familiarity', inputType: 'QC' },
        { latentElement: 'Perceived Quality', input: 'Advertising Intensity', inputType: 'QC' },
        { latentElement: 'Perceived Quality', input: 'Associations', inputType: 'LE' },
        { latentElement: 'Brand Loyalty', input: 'Behavioural Loyalty', inputType: 'LE' },
        { latentElement: 'Brand Loyalty', input: 'Attitudinal Loyalty', inputType: 'LE' },
        { latentElement: 'Brand Loyalty', input: 'Perceived Quality', inputType: 'LE' },
        { latentElement: 'Brand Loyalty', input: 'Self Reported Loyalty', inputType: 'QC' },
        { latentElement: 'Brand Loyalty', input: 'Distribution Intensity', inputType: 'QC' },
        { latentElement: 'Brand Loyalty', input: 'Associations', inputType: 'LE' },
        { latentElement: 'Brand Equity', input: 'Brand Loyalty', inputType: 'LE' },
        { latentElement: 'Brand Equity', input: 'Perceived Quality', inputType: 'LE' },
        { latentElement: 'Brand Equity', input: 'Willingness to Pay a Premium', inputType: 'LE' },
        { latentElement: 'Brand Equity', input: 'Associations', inputType: 'LE' },
        { latentElement: 'Brand Equity', input: 'Parity Preference', inputType: 'QC' },
        { latentElement: 'Brand Equity', input: 'Market Awareness', inputType: 'LE' }
    ], []);

    // Process data and create nodes/links
    const { nodes, links } = useMemo(() => {
        const nodeMap: { [key: string]: Node } = {};
        const linkArray: Link[] = [];

        // Get actual scores from API response - returns null when no data exists
        const getScore = (elementName: string, type: 'LE' | 'QC' | 'BE'): number | null => {
            if (type === 'BE') {
                return results.overallBrandEquityScore;
            } else if (type === 'LE') {
                const leData = results.latentElementScores.find(le => le.element === elementName);
                return leData ? (leData.score ?? null) : null;
            } else {
                const qcData = results.questionCategoryScores.find(qc => qc.category === elementName);
                return qcData ? (qcData.meanScore ?? null) : null;
            }
        };

        // Create latent element nodes with fixed positions
        const nodeConfig: { [key: string]: any } = {
            'Brand Equity': { x: 0, y: -380, level: 0, shape: 'star', color: '#fbbf24' },
            // Level 1 - spread out more horizontally
            'Brand Loyalty': { x: -550, y: -220, level: 1, shape: 'hexagon', color: '#10b981' },
            'Perceived Quality': { x: -250, y: -220, level: 1, shape: 'square', color: '#fb923c' },
            'Associations': { x: 250, y: -220, level: 1, shape: 'square', color: '#ef4444' },
            'Market Awareness': { x: 550, y: -220, level: 1, shape: 'square', color: '#5b9bd5' },
            // Level 2 - better spacing
            'Attitudinal Loyalty': { x: -600, y: -50, level: 2, shape: 'diamond', color: '#fb923c' },
            'Behavioural Loyalty': { x: -400, y: -50, level: 2, shape: 'diamond', color: '#fb923c' },
            // Level 3 - maximum spread to prevent overlap
            'Brand Attachment': { x: -700, y: 150, level: 3, shape: 'circle', color: '#ef4444' },
            'Willingness to Pay a Premium': { x: -450, y: 150, level: 3, shape: 'diamond', color: '#ef4444' },
            'Quality Value Tradeoff (PQV)': { x: 450, y: 150, level: 3, shape: 'circle', color: '#ef4444' },
            'Alignment Depth': { x: 700, y: 150, level: 3, shape: 'diamond', color: '#ef4444' }
        };

        // Get all latent elements from connection data
        const latentElements = new Set<string>();
        connectionData.forEach(item => latentElements.add(item.latentElement));

        // Create latent nodes with actual scores
        latentElements.forEach(element => {
            const config = nodeConfig[element] || { x: 0, y: 250, level: 4, shape: 'pill', color: '#ef4444' };
            const scoreType = element === 'Brand Equity' ? 'BE' : 'LE';
            
            nodeMap[element] = {
                id: element,
                name: element,
                type: element === 'Brand Equity' ? 'top' : 'latent',
                shape: config.shape,
                level: config.level,
                x: config.x,
                y: config.y,
                color: config.color,
                score: getScore(element, scoreType)
            };
        });

        // Group QC nodes by parent
        const qcByParent: { [key: string]: string[] } = {};
        connectionData.forEach(item => {
            if (item.inputType === 'QC') {
                if (!qcByParent[item.latentElement]) {
                    qcByParent[item.latentElement] = [];
                }
                qcByParent[item.latentElement].push(item.input);
            }
        });

        // Create QC nodes positioned around their parent
        Object.entries(qcByParent).forEach(([parentId, qcInputs]) => {
            const parent = nodeMap[parentId];
            if (parent) {
                const baseRadius = parent.shape === 'square' ? 200 :
                                   parent.shape === 'hexagon' ? 200 :
                                   parent.shape === 'circle' ? 160 :
                                   parent.shape === 'diamond' ? 200 : 140;
                
                // Calculate radius based on number of items to prevent overlap
                const itemCount = qcInputs.length;
                const minSpacing = 70;
                const circumference = itemCount * minSpacing;
                const minRadius = circumference / (2 * Math.PI);
                const radiusX = Math.max(baseRadius * 1.3, minRadius * 1.2);
                const radiusY = Math.max(baseRadius * 1.1, minRadius);
                
                qcInputs.forEach((input, index) => {
                    const angle = (index * 2 * Math.PI) / itemCount - Math.PI / 2;
                    
                    nodeMap[input] = {
                        id: input,
                        name: input,
                        type: 'qc',
                        shape: 'pill',
                        level: parent.level + 1,
                        x: parent.x! + Math.cos(angle) * radiusX,
                        y: parent.y! + Math.sin(angle) * radiusY,
                        color: '#3b82f6',
                        score: getScore(input, 'QC')
                    };
                });
            }
        });

        // Manual position overrides for specific pills
        const manualPositions: { [key: string]: { x: number; y: number } } = {
            // Example: Move "Brand Familiarity" to a specific position
            // 'Brand Familiarity': { x: -200, y: -100 },
            // 'Advertising Intensity': { x: -300, y: -50 },
            // Add more manual positions here as needed
            'Parity Preference': { x: 200, y: -420 },
            'Brand Loyalty': { x: 0, y: -200 },
            'Attitudinal Loyalty': { x: -150, y: -150 },
            'Behavioural Loyalty': { x: -150, y: 0 },
            'Perceived Quality': { x: 150, y: -120 },
            'Associations': { x: 200, y: 60 },
            'Market Awareness': { x: 200, y: 230 },
            'Alignment Depth': { x: -280, y: 150 },
            'Willingness to Pay a Premium': { x: -280, y: 300 },
            'Brand Attachment': { x: -450, y: 250 },
            'Quality Value Tradeoff (PQV)': { x: -450, y: 400 },
            'Self Reported Loyalty': { x: -400, y: -230 },
            'Standardised NPS': { x: -400, y: -190 },
            'User Congruence': { x: -400, y: -150 },
            'Brand Identification': { x: -400, y: -110 },
            'Repurchase Intention': { x: -400, y: 10 },

            'Staff Behaviour': { x: -700, y: 130 },
            'Self Congruence': { x: -700, y: 170 },
            'Brand Self Connection': { x: -700, y: 220 },
            'Prominence': { x: -700, y: 260 },
            'Perceived Value for the Cost': { x: -700, y: 400 },

            'Distribution Intensity': { x: 450, y: -150 },
            'Price': { x: 450, y: -110 },
            'Product Performance': { x: 450, y: -70 },
            'Product Richness': { x: 450, y: -30 },
            'Price Deals': { x: 450, y: 10 },
            'Advertising Content': { x: 450, y: 50 },
            'Brand Environment': { x: 450, y: 90 },
            'Brand Empathy': { x: 450, y: 130 },
            'Brand Valence': { x: 450, y: 170 },
            'Brand Trust': { x: 450, y: 210 },
            'Uniqueness': { x: 450, y: 250 },
            'Symbolic Recall': { x: 450, y: 290 },
            'Advertising Intensity': { x: 450, y: 330 },
            'Non-customer Awareness': { x: 450, y: 370 },
            'Brand Familiarity': { x: 450, y: 410 },
        };

        // Apply manual position overrides
        Object.entries(manualPositions).forEach(([pillName, position]) => {
            if (nodeMap[pillName]) {
                nodeMap[pillName].x = position.x;
                nodeMap[pillName].y = position.y;
            }
        });

        // Create links
        connectionData.forEach(item => {
            if (item.inputType === 'QC') {
                linkArray.push({
                    source: item.input,
                    target: item.latentElement,
                    type: 'qc-to-le'
                });
            } else {
                if (nodeMap[item.input]) {
                    linkArray.push({
                        source: item.input,
                        target: item.latentElement,
                        type: 'le-to-le'
                    });
                }
            }
        });

        return { nodes: Object.values(nodeMap), links: linkArray };
    }, [results, connectionData]);

    // Function to handle element click and show drawer
    const handleElementClick = (node: Node) => {
        setDrawerData(node);
        setDrawerVisible(true);
        setSelectedNode(node.id);
    };

    // Navigation history functions
    const navigateToElement = (node: Node) => {
        // Add current element to history if it exists and is different from the new one
        if (drawerData && drawerData.id !== node.id) {
            const newHistory = [...navigationHistory, drawerData];
            setNavigationHistory(newHistory);
            setCurrentHistoryIndex(newHistory.length); // Point to the new element position
        }

        // Navigate to new element
        setDrawerData(node);
        setDrawerVisible(true);
        setSelectedNode(node.id);
    };

    const navigateBack = () => {
        if (navigationHistory.length > 0 && currentHistoryIndex > 0) {
            const previousElement = navigationHistory[currentHistoryIndex - 1];
            setCurrentHistoryIndex(currentHistoryIndex - 1);
            setDrawerData(previousElement);
            setSelectedNode(previousElement.id);
        }
    };

    const clearNavigationHistory = () => {
        setNavigationHistory([]);
        setCurrentHistoryIndex(-1);
    };

    const canNavigateBack = navigationHistory.length > 0 && currentHistoryIndex > 0;

    // Get constituent details for a node
    const getConstituentDetails = (node: Node) => {
        const inputs: any[] = [];
        const outputs: any[] = [];

        // Find inputs (what feeds into this element)
        connectionData.forEach(connection => {
            if (connection.latentElement === node.name) {
                const inputNode = nodes.find(n => n.name === connection.input);
                if (inputNode) {
                    inputs.push({
                        name: connection.input,
                        type: connection.inputType,
                        score: inputNode.score
                    });
                }
            }
        });

        // Find outputs (what this element feeds into)
        connectionData.forEach(connection => {
            if (connection.input === node.name) {
                const outputNode = nodes.find(n => n.name === connection.latentElement);
                if (outputNode) {
                    outputs.push({
                        name: connection.latentElement,
                        type: outputNode.type === 'top' ? 'BE' : 'LE',
                        score: outputNode.score
                    });
                }
            }
        });

        return { inputs, outputs };
    };

    // Draw visualization
    useEffect(() => {
        if (!svgRef.current || nodes.length === 0) return;
        
        // Create or select tooltip - fixed position in top left
        let tooltip = d3.select(svgRef.current.parentElement).select('.brand-equity-tooltip-fixed');
        if (tooltip.empty()) {
            tooltip = (d3.select(svgRef.current.parentElement).append('div')
                .attr('class', 'brand-equity-tooltip-fixed')
                .style('position', 'absolute')
                .style('top', '50px')
                .style('left', '120px')
                .style('width', '250px')
                .style('padding', '16px')
                .style('background', 'rgba(255, 255, 255, 0.95)')
                .style('color', '#333')
                .style('border-radius', '8px')
                .style('font-size', '14px')
                .style('font-family', '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif')
                .style('pointer-events', 'none')
                .style('z-index', '1000')
                .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.15)')
                .style('border', '1px solid #e0e0e0')
                .style('opacity', 0)
                .style('transition', 'opacity 0.2s')) as any;
        }

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const width = 2000;
        const height = 1100;
        
        // Create a group for zooming
        const zoomGroup = svg.append('g');
        
        // Add zoom behavior
        const zoom = d3.zoom()
            .scaleExtent([0.3, 3]) // Min and max zoom levels
            .on('zoom', (event) => {
                zoomGroup.attr('transform', event.transform);
                // Save current transform
                currentTransformRef.current = event.transform;
            });
        
        // Store zoom behavior reference
        zoomBehaviorRef.current = zoom;
        
        // Add click handler to enable zoom
        svg.on('click', (event) => {
            if (!zoomEnabled) {
                setZoomEnabled(true);
                // Prevent this click from being processed as a zoom event
                event.stopPropagation();
            }
        });
        
        // Only apply zoom behavior if zoom is enabled
        if (zoomEnabled) {
            svg.call(zoom as any);
        }
        
        // Use stored transform if available, otherwise use initial config
        let transformToApply;
        if (currentTransformRef.current) {
            transformToApply = currentTransformRef.current;
        } else {
            const config = isModal ? viewConfig.modal : viewConfig.inline;
            transformToApply = d3.zoomIdentity
                .translate(width / 2, height / 2)
                .scale(config.zoom)
                .translate(-width / 2 + config.offsetX, -height / 2 + config.offsetY);
            currentTransformRef.current = transformToApply;
        }
        
        svg.call(zoom.transform as any, transformToApply);
        
        // Add zoom controls
        const zoomControls = svg.append('g')
            .attr('class', 'zoom-controls')
            .attr('transform', 'translate(20, 20)');
        
        // Zoom in button
        zoomControls.append('rect')
            .attr('width', 30)
            .attr('height', 30)
            .attr('rx', 3)
            .attr('fill', '#f0f0f0')
            .attr('stroke', '#ccc')
            .style('cursor', 'pointer')
            .on('click', () => {
                svg.transition().duration(300).call(zoom.scaleBy as any, 1.3);
            });
        
        zoomControls.append('text')
            .attr('x', 15)
            .attr('y', 20)
            .attr('text-anchor', 'middle')
            .attr('font-size', '18')
            .attr('fill', '#666')
            .style('pointer-events', 'none')
            .text('+');
        
        // Zoom out button
        zoomControls.append('rect')
            .attr('y', 35)
            .attr('width', 30)
            .attr('height', 30)
            .attr('rx', 3)
            .attr('fill', '#f0f0f0')
            .attr('stroke', '#ccc')
            .style('cursor', 'pointer')
            .on('click', () => {
                svg.transition().duration(300).call(zoom.scaleBy as any, 0.7);
            });
        
        zoomControls.append('text')
            .attr('x', 15)
            .attr('y', 55)
            .attr('text-anchor', 'middle')
            .attr('font-size', '18')
            .attr('fill', '#666')
            .style('pointer-events', 'none')
            .text('−');
        
        // Reset button
        zoomControls.append('rect')
            .attr('y', 70)
            .attr('width', 30)
            .attr('height', 30)
            .attr('rx', 3)
            .attr('fill', '#f0f0f0')
            .attr('stroke', '#ccc')
            .style('cursor', 'pointer')
            .on('click', () => {
                const config = isModal ? viewConfig.modal : viewConfig.inline;
                const resetTransform = d3.zoomIdentity
                    .translate(width / 2, height / 2)
                    .scale(config.zoom)
                    .translate(-width / 2 + config.offsetX, -height / 2 + config.offsetY);
                currentTransformRef.current = resetTransform;
                svg.transition().duration(300).call(zoom.transform as any, resetTransform);
            });
        
        zoomControls.append('text')
            .attr('x', 15)
            .attr('y', 90)
            .attr('text-anchor', 'middle')
            .attr('font-size', '12')
            .attr('fill', '#666')
            .style('pointer-events', 'none')
            .text('⟲');
        
        const g = zoomGroup.append('g')
            .attr('transform', `translate(${width/2},${height/2})`);

        // Draw links
        const linkElements = g.append('g')
            .selectAll('path')
            .data(links)
            .enter()
            .append('path')
            .attr('d', (d: Link) => {
                const source = nodes.find(n => n.id === d.source);
                const target = nodes.find(n => n.id === d.target);
                if (!source || !target) return '';
                
                // Create tube-style angular paths
                const dx = target.x! - source.x!;
                const dy = target.y! - source.y!;
                
                if (d.type === 'qc-to-le') {
                    // For QC to LE connections, avoid crossing nodes
                    const targetNode = nodes.find(n => n.id === d.target);
                    const sourceNode = nodes.find(n => n.id === d.source);
                    if (!targetNode || !sourceNode) return '';
                    
                    const isLeftSide = source.x! < 0;
                    
                    // Find all nodes that could be in the path
                    const nodeBuffer = 70; // Buffer around nodes to avoid
                    const nodesInArea = nodes.filter(n => 
                        n.id !== d.source && n.id !== d.target
                    );
                    
                    // Get all connections to the same target for spacing
                    const connectionsToSameTarget = links.filter(link => 
                        link.type === 'qc-to-le' && link.target === d.target
                    );
                    const connectionIndex = connectionsToSameTarget.findIndex(link => 
                        link.source === d.source
                    );
                    
                    // Helper function to check if channel crosses nodes
                    const checkChannelCrossing = (checkX: number, sourceY: number, targetY: number) => {
                        const minY = Math.min(sourceY, targetY) - 20;
                        const maxY = Math.max(sourceY, targetY) + 20;
                        return nodesInArea.some(node => 
                            Math.abs(node.x! - checkX) < nodeBuffer && 
                            node.y! > minY && node.y! < maxY
                        );
                    };
                    
                    // Find a clear vertical channel
                    let channelX: number;
                    const channelSpacing = 25;
                    
                    if (isLeftSide) {
                        // For left side, start from target and move left
                        const baseChannelX = target.x! - 100;
                        channelX = baseChannelX - (connectionIndex * channelSpacing);
                        
                        // Check if this channel crosses any nodes and adjust if needed
                        let foundClearChannel = false;
                        let attempts = 0;
                        while (!foundClearChannel && attempts < 10) {
                            const crossesNode = checkChannelCrossing(channelX, source.y!, target.y!);
                            
                            if (!crossesNode) {
                                foundClearChannel = true;
                            } else {
                                channelX -= channelSpacing;
                                attempts++;
                            }
                        }
                    } else {
                        // For right side, start from target and move right
                        const baseChannelX = target.x! + 100;
                        channelX = baseChannelX + (connectionIndex * channelSpacing);
                        
                        // Check if this channel crosses any nodes and adjust if needed
                        let foundClearChannel = false;
                        let attempts = 0;
                        while (!foundClearChannel && attempts < 10) {
                            const crossesNode = checkChannelCrossing(channelX, source.y!, target.y!);
                            
                            if (!crossesNode) {
                                foundClearChannel = true;
                            } else {
                                channelX += channelSpacing;
                                attempts++;
                            }
                        }
                    }
                    
                    // Create path
                    return `M${source.x},${source.y} H${channelX} V${target.y} H${target.x}`;
                } else {
                    // For LE to BE connections, create tube-style paths
                    const cornerRadius = 15;
                    
                    // Determine the source node type to handle different routing strategies
                    const sourceNode = nodes.find(n => n.id === d.source);
                    const targetNode = nodes.find(n => n.id === d.target);
                    
                    if (!sourceNode || !targetNode) return '';
                    
                    // Create offset for cleaner routing
                    let verticalOffset = 0;
                    if (sourceNode.type === 'latent') {
                        // Add offset based on which LE it is to avoid overlaps
                        const leIndex = ['Brand Loyalty', 'Attitudinal Loyalty', 'Behavioural Loyalty',
                                        'Perceived Quality', 'Associations', 'Market Awareness',
                                        'Alignment Depth'].indexOf(sourceNode.name);
                        verticalOffset = (leIndex - 3) * 10;
                    }
                    
                    // For paths going up (LE to BE)
                    if (dy < 0) {
                        const midY = source.y! + dy * 0.7 + verticalOffset;
                        
                        let path = `M${source.x},${source.y}`;
                        
                        // First go up
                        path += ` V${midY + cornerRadius}`;
                        
                        // Then curve and go horizontal
                        path += ` Q${source.x},${midY} ${source.x! + (dx > 0 ? cornerRadius : -cornerRadius)},${midY}`;
                        
                        // Go horizontal
                        path += ` H${target.x! - (dx > 0 ? cornerRadius : -cornerRadius)}`;
                        
                        // Curve and go up to target
                        path += ` Q${target.x},${midY} ${target.x},${midY - cornerRadius}`;
                        path += ` V${target.y}`;
                        
                        return path;
                    } else {
                        // For other connections, use simpler routing
                        const midX = source.x! + dx * 0.5;
                        
                        let path = `M${source.x},${source.y}`;
                        path += ` H${midX - cornerRadius}`;
                        path += ` Q${midX},${source.y} ${midX},${source.y! + cornerRadius}`;
                        path += ` V${target.y! - cornerRadius}`;
                        path += ` Q${midX},${target.y} ${midX + cornerRadius},${target.y}`;
                        path += ` H${target.x}`;
                        
                        return path;
                    }
                }
            })
            .attr('fill', 'none')
            .attr('stroke', (d: Link) => d.type === 'qc-to-le' ? '#3b82f6' : '#666')
            .attr('stroke-width', (d: Link) => d.type === 'qc-to-le' ? 1.5 : 3)
            .attr('stroke-linejoin', 'round')
            .attr('stroke-linecap', 'round')
            .attr('opacity', (d: Link) => d.type === 'qc-to-le' ? 0.3 : 0.5);

        // Draw nodes (same as your original implementation but with our data)
        const nodeGroups = g.append('g')
            .selectAll('g')
            .data(nodes)
            .enter()
            .append('g')
            .attr('transform', (d: Node) => `translate(${d.x},${d.y})`)
            .style('cursor', 'pointer')
            .on('click', (_: any, d: Node) => handleElementClick(d));

        // Add shapes (same implementation as your code...)
        nodeGroups.each(function(this: SVGGElement, d: Node) {
            const node = d3.select(this);
            
            if (d.shape === 'star') {
                const star = d3.symbol().type(d3.symbolStar).size(8000);
                node.append('path')
                    .attr('d', star)
                    .attr('fill', d.color)
                    .attr('stroke', '#fff')
                    .attr('stroke-width', 2)
                    .style('filter', 'drop-shadow(0 3px 6px rgba(0,0,0,0.2))');
            } else if (d.shape === 'hexagon') {
                const hexPath = d3.range(6).map((i: number) => {
                    const angle = (i * Math.PI) / 3 + Math.PI / 6;
                    const x = 65 * Math.cos(angle);
                    const y = 65 * Math.sin(angle);
                    return `${x},${y}`;
                }).join(' ');
                
                node.append('polygon')
                    .attr('points', hexPath)
                    .attr('fill', d.color)
                    .attr('stroke', '#fff')
                    .attr('stroke-width', 3)
                    .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))');
            } else if (d.shape === 'square') {
                node.append('rect')
                    .attr('x', -40)
                    .attr('y', -40)
                    .attr('width', 100)
                    .attr('height', 100)
                    .attr('fill', d.color)
                    .attr('stroke', '#fff')
                    .attr('stroke-width', 2)
                    .attr('rx', 12)
                    .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))');
            } else if (d.shape === 'circle') {
                node.append('circle')
                    .attr('r', 55)
                    .attr('fill', d.color)
                    .attr('stroke', '#fff')
                    .attr('stroke-width', 2)
                    .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))');
            } else if (d.shape === 'diamond') {
                node.append('rect')
                    .attr('x', -40)
                    .attr('y', -40)
                    .attr('width', 100)
                    .attr('height', 100)
                    .attr('fill', d.color)
                    .attr('stroke', '#fff')
                    .attr('stroke-width', 2)
                    .attr('transform', 'rotate(45)')
                    .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))');
            } else if (d.type === 'qc') {
                // Small pills for questionnaire items - improved visibility
                const displayName = d.name.length > 28 ? d.name.substring(0, 26) + '..' : d.name;
                const textContent = `${displayName} ${d.score}%`;
                const baseWidth = Math.max(textContent.length * 8.5 + 30, 180);
                
                const width = Math.min(180, baseWidth);
                
                node.append('rect')
                    .attr('x', -width/2)
                    .attr('y', -16)
                    .attr('width', width)
                    .attr('height', 35)
                    .attr('fill', d.color)
                    .attr('stroke', '#fff')
                    .attr('stroke-width', 1)
                    .attr('rx', 14);
            } else {
                // Regular pills
                const width = Math.max(d.name.length * 8 + 30, 120);
                node.append('rect')
                    .attr('x', -width/2)
                    .attr('y', -15)
                    .attr('width', width)
                    .attr('height', 30)
                    .attr('fill', d.color)
                    .attr('stroke', '#fff')
                    .attr('stroke-width', 2)
                    .attr('rx', 15)
                    .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))');
            }
        });

        // Add text content with proper shape-specific formatting
        nodeGroups.each(function(this: SVGGElement, d: Node) {
            const node = d3.select(this);
            
            if (d.type === 'qc') {
                const displayName = d.name.length > 28 ? d.name.substring(0, 26) + '..' : d.name;
                const score = Math.round(d.score || 0);
                
                // Two-line layout for better readability
                node.append('text')
                    .attr('text-anchor', 'middle')
                    .attr('dy', -2)
                    .style('font-size', '11px')
                    .style('fill', '#fff')
                    .style('font-weight', '600')
                    .text(displayName);
                
                node.append('text')
                    .attr('text-anchor', 'middle')
                    .attr('dy', 12)
                    .style('font-size', '12px')
                    .style('fill', '#fff')
                    .style('font-weight', 'bold')
                    .text(`${score}%`);
            } else if (d.shape === 'star') {
                // Star - name above, score in center
                node.append('text')
                    .attr('text-anchor', 'middle')
                    .attr('dy', 10)
                    .style('font-size', '26px')
                    .style('font-weight', 'bold')
                    .style('fill', '#fff')
                    .style('text-shadow', '0 2px 4px rgba(0,0,0,0.4)')
                    .text(`${Math.round(d.score || 0)}%`);
                
                node.append('text')
                    .attr('text-anchor', 'middle')
                    .attr('dy', -40)
                    .style('font-size', '16px')
                    .style('font-weight', 'bold')
                    .style('fill', '#1f2937')
                    .text(d.name);
            } else if (d.shape === 'hexagon') {
                // Hexagon - name on top lines, score on bottom
                const words = d.name.split(' ');
                words.forEach((word: string, i: number) => {
                    node.append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', -20 + i * 18)
                        .style('font-size', '14px')
                        .style('fill', '#fff')
                        .style('font-weight', '600')
                        .text(word);
                });
                
                node.append('text')
                    .attr('text-anchor', 'middle')
                    .attr('dy', 25)
                    .style('font-size', '24px')
                    .style('font-weight', 'bold')
                    .style('fill', '#fff')
                    .style('text-shadow', '0 1px 2px rgba(0,0,0,0.3)')
                    .text(`${Math.round(d.score || 0)}%`);
            } else if (d.shape === 'square') {
                // Square - handle multi-line names with score
                if (d.name.includes(' ')) {
                    const words = d.name.split(' ');
                    words.forEach((word: string, i: number) => {
                        node.append('text')
                            .attr('text-anchor', 'middle')
                            .attr('dy', -10 + i * 18)
                            .attr('dx', 10)
                            .style('font-size', '13px')
                            .style('fill', '#fff')
                            .style('font-weight', '600')
                            .text(word);
                    });
                    
                    node.append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', 35)
                        .attr('dx', 10)
                        .style('font-size', '22px')
                        .style('font-weight', 'bold')
                        .style('fill', '#fff')
                        .style('text-shadow', '0 1px 2px rgba(0,0,0,0.3)')
                        .text(`${Math.round(d.score || 0)}%`);
                } else {
                    // Single word squares
                    node.append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', 0)
                        .attr('dx', 10)
                        .style('font-size', '14px')
                        .style('fill', '#fff')
                        .style('font-weight', '600')
                        .text(d.name);
                        
                    node.append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', 25)
                        .attr('dx', 10)
                        .style('font-size', '22px')
                        .style('font-weight', 'bold')
                        .style('fill', '#fff')
                        .style('text-shadow', '0 1px 2px rgba(0,0,0,0.3)')
                        .text(`${Math.round(d.score || 0)}%`);
                }
            } else if (d.shape === 'circle') {
                // Circle - compact text layout
                let displayName = d.name;
                if (d.name === 'Quality Value Tradeoff (PQV)') {
                    displayName = 'Quality Value|Tradeoff';
                }
                
                const words = displayName.includes('|') ? displayName.split('|') : displayName.split(' ');
                if (words.length > 1) {
                    const startY = words.length === 2 ? -20 : -25;
                    words.forEach((word: string, i: number) => {
                        node.append('text')
                            .attr('text-anchor', 'middle')
                            .attr('dy', startY + i * 18)
                            .style('font-size', '13px')
                            .style('fill', '#fff')
                            .style('font-weight', '600')
                            .text(word);
                    });
                    
                    node.append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', 25)
                        .style('font-size', '22px')
                        .style('font-weight', 'bold')
                        .style('fill', '#fff')
                        .style('text-shadow', '0 1px 2px rgba(0,0,0,0.3)')
                        .text(`${Math.round(d.score || 0)}%`);
                } else {
                    node.append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', -10)
                        .style('font-size', '13px')
                        .style('fill', '#fff')
                        .style('font-weight', '600')
                        .text(displayName);
                        
                    node.append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', 15)
                        .style('font-size', '22px')
                        .style('font-weight', 'bold')
                        .style('fill', '#fff')
                        .style('text-shadow', '0 1px 2px rgba(0,0,0,0.3)')
                        .text(`${Math.round(d.score || 0)}%`);
                }
            } else if (d.shape === 'diamond') {
                // Diamond - rotated text
                const words = d.name.split(' ');
                if (words.length >= 3) {
                    // Three+ words
                    node.append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', 5)
                        .style('font-size', '11px')
                        .style('fill', '#fff')
                        .style('font-weight', '600')
                        .text(words.slice(0, -1).join(' '));
                    
                    node.append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', 20)
                        .style('font-size', '11px')
                        .style('fill', '#fff')
                        .style('font-weight', '600')
                        .text(words[words.length - 1]);
                        
                    node.append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', 45)
                        .style('font-size', '20px')
                        .style('font-weight', 'bold')
                        .style('fill', '#fff')
                        .style('text-shadow', '0 1px 2px rgba(0,0,0,0.3)')
                        .text(`${Math.round(d.score || 0)}%`);
                } else {
                    // Two words
                    words.forEach((word: string, i: number) => {
                        node.append('text')
                            .attr('text-anchor', 'middle')
                            .attr('dy', 0 + i * 16)
                            .style('font-size', '12px')
                            .style('fill', '#fff')
                            .style('font-weight', '600')
                            .text(word);
                    });
                    
                    node.append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', 40)
                        .style('font-size', '20px')
                        .style('font-weight', 'bold')
                        .style('fill', '#fff')
                        .style('text-shadow', '0 1px 2px rgba(0,0,0,0.3)')
                        .text(`${Math.round(d.score || 0)}%`);
                }
            } else {
                // Regular pills
                node.append('text')
                    .attr('text-anchor', 'middle')
                    .attr('dy', 5)
                    .style('font-size', '12px')
                    .style('fill', '#fff')
                    .style('font-weight', 'normal')
                    .text(`${d.name} (${Math.round(d.score || 0)}%)`);
            }
        });

        // Add hover interactions (same as your original implementation)
        nodeGroups
            .on('mouseenter', function(this: SVGGElement, event: any, d: Node) {
                const hoveredGroup = d3.select(this);

                const scoreColour = d.type === 'top' ? '#fbbf24' : d.type === 'latent' ? (d.name === 'Attitudinal Loyalty' || d.name === 'Perceived Quality' || d.name === 'Behavioural Loyalty' ? '#fb923c' : d.name === 'Brand Loyalty' ? '#10b981' : '#ef4444') :
                    '#3b82f6'
                
                hoveredGroup
                    .transition()
                    .duration(200)
                    .attr('transform', (node: any) => `translate(${node.x},${node.y}) scale(1.1)`);
                
                const mouseoverDescription = dynamicElementDescriptions[d.name]?.mouseover || '';
                
                tooltip
                    .html(`
                        <div style="font-weight: bold; margin-bottom: 8px; font-size: 16px; color: #1f2937;">${d.name}</div>
                        ${mouseoverDescription ? `<div style="margin-bottom: 8px; font-size: 12px; color: #4b5563; font-style: italic;">${mouseoverDescription}</div>` : ''}
                        <div style="display: flex; justify-content: space-between; align-items: center;">
                            <span style="color: #6b7280;">Score:</span>
                            <span style="color: ${scoreColour}; font-weight: bold; font-size: 24px;">${(d.score || 0).toFixed(1)}%</span>
                        </div>
                        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #9ca3af;">
                            ${d.type === 'top' ? 'Brand Equity' : d.type === 'latent' ? 'Latent Element' : 'Measured Element'}
                        </div>
                    `)
                    .transition()
                    .duration(200)
                    .style('opacity', 1);
                
                const connectedIds = new Set([d.id]);
                links.forEach(link => {
                    if (link.source === d.id) connectedIds.add(link.target);
                    if (link.target === d.id) connectedIds.add(link.source);
                });
                
                nodeGroups
                    .transition()
                    .duration(200)
                    .style('opacity', (node: Node) => connectedIds.has(node.id) ? 1 : 0.2);
                
                linkElements
                    .transition()
                    .duration(200)
                    .style('opacity', (link: Link) => 
                        (link.source === d.id || link.target === d.id) ? 1 : 0.1
                    );
            })
            .on('mouseleave', function(this: SVGGElement) {
                d3.select(this)
                    .transition()
                    .duration(200)
                    .attr('transform', (node: any) => `translate(${node.x},${node.y}) scale(1)`);
                
                tooltip
                    .transition()
                    .duration(200)
                    .style('opacity', 0);
                
                nodeGroups
                    .transition()
                    .duration(200)
                    .style('opacity', 1);
                    
                linkElements
                    .transition()
                    .duration(200)
                    .style('opacity', (d: Link) => d.type === 'qc-to-le' ? 0.3 : 0.5);
            });

    }, [nodes, links, isModal, viewConfig, dynamicElementDescriptions, zoomEnabled]);

    // Cleanup tooltip on unmount
    useEffect(() => {
        return () => {
            d3.select('.brand-equity-tooltip-fixed').remove();
        };
    }, []);

    return (
        <Card 
            style={{ 
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                border: 'none',
                overflow: 'hidden'
            }}
        >
            <div style={{ 
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '16px',
                padding: '24px',
                margin: '-24px',
                backdropFilter: 'blur(10px)'
            }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    {/*
                    <h2 style={{
                        color: '#1f2937',
                        fontSize: '24px',
                        fontWeight: '700',
                        margin: '0 0 8px 0'
                    }}>
                        Brand Equity Flow Model
                    </h2>
                    <p style={{ 
                        color: '#6b7280', 
                        fontSize: '14px',
                        margin: 0
                    }}>
                        Interactive visualization showing relationships between Question Categories and Latent Elements
                    </p>
                    */}
                </div>

                <div style={{ 
                    width: '100%',
                    height: isModal ? 'calc(85vh - 120px)' : '600px',
                    overflow: (() => {
                        const config = viewConfig[isModal ? 'modal' : 'inline'];
                        if (config.hideHorizontalScroll && config.hideVerticalScroll) return 'hidden';
                        if (config.hideHorizontalScroll) return 'hidden auto';
                        if (config.hideVerticalScroll) return 'auto hidden';
                        return 'auto';
                    })(),
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa',
                    position: 'relative'
                }}>
                    {!zoomEnabled ? (
                        <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            background: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            zIndex: 1000,
                            pointerEvents: 'none'
                        }}>
{intl.formatMessage({ id: 'brandEquity.visualization.clickToEnableZoom' })}
                        </div>
                    ) : (
                        <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            background: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            zIndex: 1000,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setZoomEnabled(false);
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
                        }}
                        >
                            <span style={{ fontSize: '14px' }}>🔒</span>
                            {intl.formatMessage({ id: 'brandEquity.visualization.lockZoomPan' })}
                        </div>
                    )}
                    <svg
                        ref={svgRef}
                        width="100%"
                        height="100%"
                        viewBox="0 0 2000 1100"
                        style={{ 
                            display: 'block',
                            minWidth: '2000px',
                            minHeight: '1100px'
                        }}
                    />
                </div>

                {/* Legend */}
                <div style={{ 
                    marginTop: '24px',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '24px',
                    flexWrap: 'wrap'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '20px', height: '20px', backgroundColor: '#fbbf24', clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }} />
                        <span style={{ fontSize: '14px', color: '#374151' }}>Brand Equity</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#10b981' }} />
                        <span style={{ fontSize: '14px', color: '#374151' }}>Latent Elements</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
                        <span style={{ fontSize: '14px', color: '#374151' }}>Latent Elements</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '16px', height: '8px', borderRadius: '4px', backgroundColor: '#3b82f6' }} />
                        <span style={{ fontSize: '14px', color: '#374151' }}>Measured Elements</span>
                    </div>
                </div>
            </div>
            
            {/* Constituent Details Drawer */}
            <Drawer
                title={
                    <div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '6px 14px',
                            backgroundColor: (() => {
                                const backgroundColor = drawerData?.type === 'top' ? 'rgba(251, 191, 36, 0.2)' :
                                    drawerData?.type === 'latent' ?
                                        (drawerData?.name === 'Attitudinal Loyalty' || drawerData?.name === 'Perceived Quality' || drawerData?.name === 'Behavioural Loyalty' ? 'rgba(251, 146, 60, 0.2)' :
                                         drawerData?.name === 'Brand Loyalty' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)') :
                                        'rgba(59, 130, 246, 0.2)';
                                return backgroundColor.replace('0.2)', '0.3)');
                            })(),
                            borderRadius: '8px',
                            marginBottom: '8px'
                        }}>
                            {canNavigateBack && (
                                <Button
                                    type="text"
                                    icon={<ArrowLeftOutlined />}
                                    onClick={navigateBack}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '32px',
                                        height: '32px',
                                        minWidth: '32px',
                                        borderRadius: '6px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        border: '1px solid rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                />
                            )}
                            <div style={{ flex: 1 }}>
                                <div style={{
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    color: '#1f2937',
                                    marginBottom: '2px'
                                }}>
                                    {drawerData?.name}
                                </div>
                            </div>
                            <div style={{ flexShrink: 0 }}>
                                {(() => {
                                    const score = drawerData?.score ?? null;
                                    const backgroundColor = drawerData?.type === 'top' ? 'rgba(251, 191, 36, 0.2)' :
                                        drawerData?.type === 'latent' ?
                                            (drawerData?.name === 'Attitudinal Loyalty' || drawerData?.name === 'Perceived Quality' || drawerData?.name === 'Behavioural Loyalty' ? 'rgba(251, 146, 60, 0.2)' :
                                             drawerData?.name === 'Brand Loyalty' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)') :
                                            'rgba(59, 130, 246, 0.2)';
                                    const fillColor = drawerData?.type === 'top' ? '#fbbf24' :
                                        drawerData?.type === 'latent' ?
                                            (drawerData?.name === 'Attitudinal Loyalty' || drawerData?.name === 'Perceived Quality' || drawerData?.name === 'Behavioural Loyalty' ? '#fb923c' :
                                             drawerData?.name === 'Brand Loyalty' ? '#10b981' : '#ef4444') :
                                            '#3b82f6';

                                    const bipolarHtml = createBipolarProgressBar(score, backgroundColor, fillColor, drawerData?.name);
                                    return (
                                        <div dangerouslySetInnerHTML={{ __html: bipolarHtml }} />
                                    );
                                })()}
                            </div>
                        </div>
                        <Text style={{textAlign: 'left', display: 'block', width: '100%', marginTop: '4px'}}>
                            {dynamicElementDescriptions[drawerData?.name || '']?.sidebar || ''}
                        </Text>
                        {drawerData?.name === 'Price Deals' && (
                            <Text type="secondary" style={{
                                textAlign: 'left', 
                                display: 'block', 
                                width: '100%', 
                                marginTop: '8px',
                                fontSize: '12px',
                                fontStyle: 'italic'
                            }}>
                                Note: This measure ranges from -100% to 0%
                            </Text>
                        )}
                    </div>
                }
                placement="right"
                closable={false}
                onClose={() => {
                    setDrawerVisible(false);
                    clearNavigationHistory();
                }}
                open={drawerVisible}
                width={410}
                styles={{header: {display: 'none'}, body: {padding: 0}}}
            >
                {drawerData && (
                    <div style={{ width: '100%' }}>
                        {/* Button Row */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 16px 0 16px'
                        }}>
                            {canNavigateBack && (
                                <Button
                                    type="text"
                                    icon={<ArrowLeftOutlined />}
                                    onClick={navigateBack}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '32px',
                                        height: '32px',
                                        minWidth: '32px',
                                        borderRadius: '6px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        border: '1px solid rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }}
                                />
                            )}
                            <Button
                                type="text"
                                onClick={() => {
                                    setDrawerVisible(false);
                                    clearNavigationHistory();
                                }}
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    minWidth: '32px',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '16px',
                                    color: '#6b7280',
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    border: '1px solid rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                                    e.currentTarget.style.color = '#dc2626';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                                    e.currentTarget.style.color = '#6b7280';
                                }}
                            >
                                ✕
                            </Button>
                        </div>

                        {/* Title and Content Section */}
                        <div style={{
                            padding: '16px 20px 12px 20px',
                            borderBottom: '1px solid #f0f0f0',
                            marginBottom: '16px',
                            textAlign: 'left'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '8px 16px',
                                backgroundColor: (() => {
                                    const backgroundColor = drawerData?.type === 'top' ? 'rgba(251, 191, 36, 0.2)' :
                                        drawerData?.type === 'latent' ?
                                            (drawerData?.name === 'Attitudinal Loyalty' || drawerData?.name === 'Perceived Quality' || drawerData?.name === 'Behavioural Loyalty' ? 'rgba(251, 146, 60, 0.2)' :
                                             drawerData?.name === 'Brand Loyalty' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)') :
                                            'rgba(59, 130, 246, 0.2)';
                                    return backgroundColor.replace('0.2)', '0.3)');
                                })(),
                                borderRadius: '8px',
                                marginBottom: '12px'
                            }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        color: '#1f2937',
                                        marginBottom: '2px'
                                    }}>
                                        {drawerData?.name}
                                    </div>
                                </div>
                                <div style={{ flexShrink: 0 }}>
                                    {(() => {
                                        const score = drawerData?.score ?? null;
                                        const backgroundColor = drawerData?.type === 'top' ? 'rgba(251, 191, 36, 0.2)' :
                                            drawerData?.type === 'latent' ?
                                                (drawerData?.name === 'Attitudinal Loyalty' || drawerData?.name === 'Perceived Quality' || drawerData?.name === 'Behavioural Loyalty' ? 'rgba(251, 146, 60, 0.2)' :
                                                 drawerData?.name === 'Brand Loyalty' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)') :
                                                'rgba(59, 130, 246, 0.2)';
                                        const fillColor = drawerData?.type === 'top' ? '#fbbf24' :
                                            drawerData?.type === 'latent' ?
                                                (drawerData?.name === 'Attitudinal Loyalty' || drawerData?.name === 'Perceived Quality' || drawerData?.name === 'Behavioural Loyalty' ? '#fb923c' :
                                                 drawerData?.name === 'Brand Loyalty' ? '#10b981' : '#ef4444') :
                                                '#3b82f6';

                                        return (
                                            <div dangerouslySetInnerHTML={{
                                                __html: createBipolarProgressBar(score, backgroundColor, fillColor, drawerData?.name)
                                            }} />
                                        );
                                    })()}
                                </div>
                            </div>
                            <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
                                {dynamicElementDescriptions[drawerData?.name || '']?.sidebar || ''}
                            </div>
                            {drawerData?.name === 'Price Deals' && (
                                <Text style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    fontStyle: 'italic',
                                    color: '#6b7280',
                                    marginTop: '8px'
                                }}>
                                    Note: This measure ranges from -100% to 0%
                                </Text>
                            )}
                        </div>

                        <div style={{ padding: '0 20px' }}>
                        {/* AI Summary Section - Positioned prominently after title/description */}
                        {(() => {
                            let aiSummary: string | undefined = undefined;
                            
                            // Get AI summary based on node type
                            if (drawerData.type === 'qc') {
                                const qcData = results.questionCategoryScores.find(qc => qc.category === drawerData.name);
                                aiSummary = qcData?.aiSummary;
                            } else if (drawerData.type === 'latent') {
                                const leData = results.latentElementScores.find(le => le.element === drawerData.name);
                                aiSummary = leData?.aiSummary;
                            } else if (drawerData.type === 'top' && drawerData.name === 'Brand Equity') {
                                // Brand Equity is stored in latentElementScores but has type 'top'
                                const beData = results.latentElementScores.find(le => le.element === 'Brand Equity');
                                aiSummary = beData?.aiSummary;
                            }
                            
                            // Always show the AI summary section, with fallback text if needed
                            const displaySummary = currentAiSummary || aiSummary || intl.formatMessage({ id: 'brandEquity.aiSummary.awaitingResponses' });
                            return (
                                    <div style={{ 
                                        marginBottom: '24px',
                                        padding: '16px',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0',
                                        borderLeft: '4px solid #3b82f6'
                                    }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            marginBottom: '8px' 
                                        }}>
                                            <span style={{ 
                                                fontSize: '16px',
                                                marginRight: '8px',
                                                color: '#3b82f6'
                                            }}>
                                                💭
                                            </span>
                                            <Title level={5} style={{ 
                                                color: '#1e293b', 
                                                margin: 0,
                                                fontSize: '14px',
                                                fontWeight: '600'
                                            }}>
                                                First Thoughts
                                            </Title>
                                        </div>
                                        <div style={{ 
                                            color: '#475569',
                                            fontSize: '14px',
                                            lineHeight: '1.5',
                                            textAlign: 'left'
                                        }}>
                                            <ReactMarkdown
                                                components={{
                                                    p: ({children}) => <span style={{ display: 'block', marginBottom: '8px' }}>{children}</span>,
                                                    strong: ({children}) => <strong style={{ fontWeight: 600, color: '#1e293b' }}>{children}</strong>,
                                                    em: ({children}) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                    ul: ({children}) => <ul style={{ paddingLeft: '16px', marginBottom: '8px' }}>{children}</ul>,
                                                    ol: ({children}) => <ol style={{ paddingLeft: '16px', marginBottom: '8px' }}>{children}</ol>,
                                                    li: ({children}) => <li style={{ marginBottom: '4px' }}>{children}</li>
                                                }}
                                            >
                                                {displaySummary}
                                            </ReactMarkdown>
                                        </div>
                                        <Button
                                            type="default"
                                            size="small"
                                            loading={isReanalyzing}
                                            style={{ 
                                                marginTop: '12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                            onClick={async () => {
                                                if (!drawerData || !user || !organisationId) return;
                                                
                                                setIsReanalyzing(true);
                                                try {
                                                    // Determine element type and name
                                                    let elementType: 'measure' | 'latent';
                                                    let elementName: string;
                                                    
                                                    if (drawerData.type === 'qc') {
                                                        elementType = 'measure';
                                                        elementName = drawerData.name;
                                                    } else if (drawerData.type === 'latent') {
                                                        elementType = 'latent';
                                                        elementName = drawerData.name;
                                                    } else {
                                                        // For 'top' type (Brand Equity)
                                                        elementType = 'latent';
                                                        elementName = 'Brand Equity';
                                                    }
                                                    
                                                    const response = await reanalyzeElement({
                                                        jwtToken: user.jwtToken,
                                                        organisationId,
                                                        elementType,
                                                        elementName,
                                                        brandEquityData: results,
                                                    }).unwrap();
                                                    
                                                    // Update the current AI summary with the new one
                                                    setCurrentAiSummary(response.aiSummary);
                                                } catch (error) {
                                                    console.error('Failed to re-analyze element:', error);
                                                } finally {
                                                    setIsReanalyzing(false);
                                                }
                                            }}
                                        >
                                            {!isReanalyzing && <ReloadOutlined />}
                                            Analyse Again
                                        </Button>
                                    </div>
                                );
                        })()}

                        {/* Constituent Details */}
                        {(() => {
                            const details = getConstituentDetails(drawerData);
                            return (
                                <div>
                                    {/* Inputs */}
                                    {details.inputs.length > 0 && (
                                        <div style={{ marginBottom: '24px', width: '99%' }}>
                                            <Title level={5} style={{ color: '#1f2937', marginBottom: '12px' }}>
                                                Input Elements ({details.inputs.length})
                                            </Title>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                                {details.inputs.map((input: any, index: number) => (
                                                    <div 
                                                        key={index}
                                                        style={{ 
                                                            padding: '12px',
                                                            backgroundColor: (() => {
                                                                const backgroundColor = input.type === 'BE' ? 'rgba(251, 191, 36, 0.2)' :
                                                                    input.type === 'LE' ? 
                                                                        (input.name === 'Attitudinal Loyalty' || input.name === 'Perceived Quality' || input.name === 'Behavioural Loyalty' ? 'rgba(251, 146, 60, 0.2)' : 
                                                                         input.name === 'Brand Loyalty' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)') :
                                                                        'rgba(59, 130, 246, 0.2)';
                                                                return backgroundColor.replace('0.2)', '0.3)');
                                                            })(),
                                                            borderRadius: '6px',
                                                            border: '1px solid #e5e7eb',
                                                            width: '100%',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onClick={() => {
                                                            // Find the node data for this input element
                                                            const inputNode = nodes.find(n => n.name === input.name);
                                                            if (inputNode) {
                                                                navigateToElement(inputNode);
                                                            }
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = 'none';
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{
                                                                    fontSize: '14px',
                                                                    fontWeight: '500',
                                                                    color: '#1f2937',
                                                                    marginBottom: '4px'
                                                                }}>
                                                                    {input.name}
                                                                </div>
                                                            </div>
                                                            <div style={{ textAlign: 'center', flexShrink: 0 }}>
                                                                {(() => {
                                                                    const score = input.score ?? null;
                                                                    const backgroundColor = input.type === 'BE' ? 'rgba(251, 191, 36, 0.2)' :
                                                                        input.type === 'LE' ? 
                                                                            (input.name === 'Attitudinal Loyalty' || input.name === 'Perceived Quality' || input.name === 'Behavioural Loyalty' ? 'rgba(251, 146, 60, 0.2)' : 
                                                                             input.name === 'Brand Loyalty' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)') :
                                                                            'rgba(59, 130, 246, 0.2)';
                                                                    const fillColor = input.type === 'BE' ? '#fbbf24' :
                                                                        input.type === 'LE' ?
                                                                            (input.name === 'Attitudinal Loyalty' || input.name === 'Perceived Quality' || input.name === 'Behavioural Loyalty' ? '#fb923c' :
                                                                             input.name === 'Brand Loyalty' ? '#10b981' : '#ef4444') :
                                                                            '#3b82f6';

                                                                    const bipolarHtml = createBipolarProgressBar(score, backgroundColor, fillColor, input.name);
                                                                    return (
                                                                        <div dangerouslySetInnerHTML={{ __html: bipolarHtml }} />
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Outputs */}
                                    {details.outputs.length > 0 && (
                                        <div style={{ width: '99%' }}>
                                            <Title level={5} style={{ color: '#1f2937', marginBottom: '12px' }}>
                                                Feeds Into ({details.outputs.length})
                                            </Title>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                                {details.outputs.map((output: any, index: number) => (
                                                    <div 
                                                        key={index}
                                                        style={{ 
                                                            padding: '12px',
                                                            backgroundColor: (() => {
                                                                const backgroundColor = output.type === 'BE' ? 'rgba(251, 191, 36, 0.2)' :
                                                                    output.type === 'LE' ? 
                                                                        (output.name === 'Attitudinal Loyalty' || output.name === 'Perceived Quality' || output.name === 'Behavioural Loyalty' ? 'rgba(251, 146, 60, 0.2)' : 
                                                                         output.name === 'Brand Loyalty' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)') :
                                                                        'rgba(59, 130, 246, 0.2)';
                                                                return backgroundColor.replace('0.2)', '0.3)');
                                                            })(),
                                                            borderRadius: '6px',
                                                            border: '1px solid #e5e7eb',
                                                            width: '100%',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onClick={() => {
                                                            // Find the node data for this output element
                                                            const outputNode = nodes.find(n => n.name === output.name);
                                                            if (outputNode) {
                                                                navigateToElement(outputNode);
                                                            }
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = 'none';
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{
                                                                    fontSize: '14px',
                                                                    fontWeight: '500',
                                                                    color: '#1f2937',
                                                                    marginBottom: '4px'
                                                                }}>
                                                                    {output.name}
                                                                </div>
                                                            </div>
                                                            <div style={{ textAlign: 'center', flexShrink: 0 }}>
                                                                {(() => {
                                                                    const score = output.score || 0;
                                                                    const backgroundColor = output.type === 'BE' ? 'rgba(251, 191, 36, 0.2)' :
                                                                        output.type === 'LE' ? 
                                                                            (output.name === 'Attitudinal Loyalty' || output.name === 'Perceived Quality' || output.name === 'Behavioural Loyalty' ? 'rgba(251, 146, 60, 0.2)' : 
                                                                             output.name === 'Brand Loyalty' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)') :
                                                                            'rgba(59, 130, 246, 0.2)';
                                                                    const fillColor = output.type === 'BE' ? '#fbbf24' :
                                                                        output.type === 'LE' ?
                                                                            (output.name === 'Attitudinal Loyalty' || output.name === 'Perceived Quality' || output.name === 'Behavioural Loyalty' ? '#fb923c' :
                                                                             output.name === 'Brand Loyalty' ? '#10b981' : '#ef4444') :
                                                                            '#3b82f6';

                                                                    const bipolarHtml = createBipolarProgressBar(score, backgroundColor, fillColor, output.name);
                                                                    return (
                                                                        <div dangerouslySetInnerHTML={{ __html: bipolarHtml }} />
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Question Category Input Questions */}
                                    {drawerData.type === 'qc' && (() => {
                                        const qcData = results.questionCategoryScores.find(qc => qc.category === drawerData.name);
                                        const totalResponses = qcData?.input?.reduce((sum, q) => sum + q.responseCount, 0) || 0;
                                        const avgResponses = qcData?.input?.length ? Math.round(totalResponses / qcData.input.length) : 0;
                                        
                                        return qcData?.input && qcData.input.length > 0 ? (
                                            <div style={{ marginTop: '24px', width: '100%' }}>
                                                <Title level={5} style={{ color: '#1f2937', marginBottom: '12px' }}>
                                                    Survey Statements ({qcData.input.length})
                                                </Title>
                                                
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                                    {qcData.input.map((question, index) => {
                                                        const isLowResponse = question.responseCount < 20;
                                                        return (
                                                            <div
                                                                key={question.id}
                                                                style={{
                                                                    padding: '12px',
                                                                    backgroundColor: isLowResponse ? '#fef3c7' : '#f3f4f6',
                                                                    borderRadius: '6px',
                                                                    border: `1px solid ${isLowResponse ? '#fbbf24' : '#3b82f6'}`,
                                                                    width: '100%',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease'
                                                                }}
                                                                onClick={() => {
                                                                    setSelectedStatement(question);
                                                                    setSelectedStatementCategory(drawerData?.name || '');
                                                                    setStatementModalVisible(true);
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                                    e.currentTarget.style.boxShadow = 'none';
                                                                }}
                                                            >
                                                                <div style={{ marginBottom: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                        <Text style={{ color: '#6b7280', fontSize: '12px' }}>
                                                                            Statement {index + 1}
                                                                        </Text>
                                                                        <Tag 
                                                                            color="blue" 
                                                                            style={{ 
                                                                                fontSize: '10px', 
                                                                                lineHeight: '14px',
                                                                                padding: '0 4px',
                                                                                margin: 0,
                                                                                width: 'fit-content'
                                                                            }}
                                                                        >
                                                                            {question.id}
                                                                        </Tag>
                                                                    </div>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', textAlign: 'right' }}>
                                                                        <Text style={{
                                                                            color: isLowResponse ? '#92400e' : '#6b7280',
                                                                            fontSize: '12px',
                                                                            fontWeight: isLowResponse ? 'bold' : 'normal'
                                                                        }}>
                                                                            {question.responseCount} response{question.responseCount !== 1 ? 's' : ''}
                                                                        </Text>
                                                                        {isLowResponse && (
                                                                            <Tag
                                                                                color="warning"
                                                                                style={{
                                                                                    fontSize: '11px',
                                                                                    marginRight: -3,  // Override any default margin
                                                                                    marginLeft: 'auto'
                                                                                }}
                                                                            >
                                                                                Low Response
                                                                            </Tag>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <Text style={{ color: '#1f2937', display: 'block', marginBottom: '8px', textAlign: 'left' }}>
                                                                    {question.text}
                                                                </Text>
                                                                <StrengthSlider 
                                                                    value={question.meanScore} 
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {/* Response Count Speedometer */}
                                                <div style={{ marginTop: '20px' }}>
                                                    <ResponseSpeedometer responseCount={avgResponses} />
                                                </div>
                                            </div>
                                        ) : null;
                                    })()}

                                    {/* No connections message */}
                                    {details.inputs.length === 0 && details.outputs.length === 0 && (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: '40px 20px',
                                            color: '#6b7280'
                                        }}>
                                            <Text>No constituent relationships found for this element.</Text>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                        </div>
                    </div>
                )}
            </Drawer>

            {/* Statement Detail Modal */}
            <StatementDetailModal
                visible={statementModalVisible}
                onClose={() => setStatementModalVisible(false)}
                statement={selectedStatement}
                categoryName={selectedStatementCategory}
                isAdmin={isAdmin}
            />
        </Card>
    );
};

export default BrandEquityD3Visualization;